import React, { useMemo, useState } from "react";

import {
  childrensBookIllustrationsByUnit,
  currentChildrensBookCover,
} from "../utils/childrensBookAssetState";

import {
  plainTextFromManuscriptEntry,
} from "../utils/childrensBookManuscriptContent";

import {
  buildChildrensBookUnitDiagnostics,
  summarizeChildrensBookDiagnostics,
} from "../utils/childrensBookUnitDiagnostics";

import {
  buildPublicationHandoffSummary,
} from "./ChildrensBookPublicationHandoff";

function textPresent(value) {
  return Boolean(
    String(value ?? "").trim()
  );
}

function unitRequiresText(unit) {
  return (
    String(
      unit?.textAmount || ""
    )
      .trim()
      .toLowerCase() !== "none"
  );
}

function decisionMade(value) {
  const normalized =
    String(value ?? "").trim();

  return (
    textPresent(normalized) &&
    normalized !== "not_decided"
  );
}

function trimSizeComplete(
  trimSize,
  customTrimSize
) {
  const normalized =
    String(trimSize ?? "").trim();

  if (!decisionMade(normalized)) {
    return false;
  }

  if (
    normalized ===
    "custom_dimensions"
  ) {
    return textPresent(
      customTrimSize
    );
  }

  return true;
}

const STATUS_TEXT = {
  en: {
    not_started: "Not started",
    in_progress: "In progress",
    needs_attention: "Needs attention",
    ready: "Ready",
    published: "Published",
    not_published: "Not published",
    planned_later: "Planned later",
  },
  fr: {
    not_started: "Non commencé",
    in_progress: "En cours",
    needs_attention: "À compléter",
    ready: "Prêt",
    published: "Publié",
    not_published: "Non publié",
    planned_later: "Prévu plus tard",
  },
};

function statusStyle(status) {
  const base = {
    display: "inline-flex",
    padding: "5px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
  };

  if (
    status === "ready" ||
    status === "published"
  ) {
    return { ...base, background: "#dcfce7", color: "#166534" };
  }

  if (status === "needs_attention") {
    return { ...base, background: "#ffedd5", color: "#9a3412" };
  }

  if (status === "in_progress") {
    return { ...base, background: "#fef3c7", color: "#92400e" };
  }

  return { ...base, background: "#e2e8f0", color: "#475569" };
}

function BlueprintCard({
  title,
  description,
  detail,
  status,
  statusText,
  actionLabel,
  onOpen,
  secondaryActionLabel = "",
  onSecondaryOpen,
  disabled = false,
}) {
  return (
    <article
      className="ns-card ns-nbb-workspace-card"
      style={{
        padding: 20,
        borderRadius: 18,
        border: "1px solid rgba(234,88,12,0.20)",
        display: "grid",
        gap: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h3 className="ns-card-title ns-nbb-card-title" style={{ margin: 0 }}>
          {title}
        </h3>
        <span style={statusStyle(status)}>{statusText}</span>
      </div>

      <p className="ns-card-meta" style={{ margin: 0, lineHeight: 1.6 }}>
        {description}
      </p>

      <div
        className="ns-nbb-inner-panel"
        style={{
          padding: 12,
          borderRadius: 12,
          background: "#f8fafc",
          color: "#334155",
          fontWeight: 700,
          lineHeight: 1.5,
        }}
      >
        {detail}
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <button
          className={disabled ? "ns-button ns-button-outline" : "ns-button ns-button-primary"}
          type="button"
          disabled={disabled}
          onClick={onOpen}
        >
          {actionLabel}
        </button>

        {secondaryActionLabel &&
        typeof onSecondaryOpen ===
          "function" ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onSecondaryOpen}
          >
            {secondaryActionLabel}
          </button>
        ) : null}
      </div>
    </article>
  );
}

export default function ChildrensBookBlueprintOverview({
  lang = "en",
  projectPlan,
  childrensBookAssets = [],
  onOpenProjectPlan,
  onOpenBookMap,
  onOpenCoverPlan,
  onOpenManuscriptPlan,
  onOpenIllustrationPlan,
  onOpenBookAssembly,
  onOpenPublicationReadiness,
  onOpenPublicationHandoff,
  onPublish,
  onOpenPublishedBook,
  onReturn,
  onExit,
}) {
  const isFR = String(lang).toLowerCase() === "fr";
  const locale = isFR ? "fr" : "en";

  const bookUnits = useMemo(
    () =>
      Array.isArray(projectPlan.bookMap?.units)
        ? projectPlan.bookMap.units
        : [],
    [projectPlan.bookMap?.units]
  );

  const projectStatus = useMemo(() => {
    const checks = [
      textPresent(
        projectPlan.identity?.title
      ),
      textPresent(
        projectPlan.audience?.ageRange
      ),
      decisionMade(
        projectPlan.audience
          ?.readingLevel
      ),
      textPresent(
        projectPlan.bookForm?.type
      ),
      trimSizeComplete(
        projectPlan.visualFormat
          ?.trimSize,
        projectPlan.visualFormat
          ?.customTrimSize
      ),
      textPresent(
        projectPlan.collaboration?.mode
      ),
    ];

    const completed =
      checks.filter(Boolean).length;

    if (completed === 0) {
      return "not_started";
    }

    if (
      completed === checks.length
    ) {
      return "ready";
    }

    return "in_progress";
  }, [projectPlan]);

  const bookMapStatus =
    useMemo(
      () =>
        bookUnits.length === 0
          ? "not_started"
          : "ready",
      [bookUnits]
    );

  const coverStatus =
    useMemo(() => {
      const required = [
        projectPlan.coverPlan?.title,
        projectPlan.coverPlan
          ?.authorName,
      ];

      const completed =
        required.filter(
          (value) =>
            String(
              value ?? ""
            ).trim()
        ).length;

      if (completed === 0) {
        return "not_started";
      }

      return completed ===
        required.length
          ? "ready"
          : "in_progress";
    }, [projectPlan]);

  const estimatedPages = bookUnits.reduce(
    (total, unit) => total + (unit.unitType === "double_page_spread" ? 2 : 1),
    0
  );

  const manuscriptEntries =
    Array.isArray(projectPlan.manuscriptPlan?.entries)
      ? projectPlan.manuscriptPlan.entries
      : [];

  const completedManuscriptIds = new Set(
    manuscriptEntries
      .filter(
        (entry) =>
          textPresent(
            plainTextFromManuscriptEntry(
              entry
            )
          ) &&
          entry.status === "complete"
      )
      .map((entry) => entry.unitId)
  );

  const manuscriptStatus =
    bookUnits.length === 0
      ? "not_started"
      : bookUnits.every(
          (unit) =>
            !unitRequiresText(unit) ||
            completedManuscriptIds.has(
              unit.id
            )
        )
        ? "ready"
        : manuscriptEntries.length > 0
          ? "in_progress"
          : "not_started";

  const requiredIllustrationUnits =
    bookUnits.filter(
      (unit) =>
        unit.illustrationPlan &&
        unit.illustrationPlan !== "none"
    );

  const illustrationEntries =
    Array.isArray(projectPlan.illustrationPlan?.entries)
      ? projectPlan.illustrationPlan.entries
      : [];

  const completedIllustrationIds =
    new Set(
      illustrationEntries
        .filter(
          (entry) =>
            entry.status ===
              "complete"
        )
        .map(
          (entry) =>
            entry.unitId
        )
    );

  const illustrationStatus =
    bookUnits.length === 0
      ? "not_started"
      : requiredIllustrationUnits.length === 0 ||
          requiredIllustrationUnits.every((unit) =>
            completedIllustrationIds.has(unit.id)
          )
        ? "ready"
        : illustrationEntries.length > 0
          ? "in_progress"
          : "not_started";

  const activeCover =
    currentChildrensBookCover(
      childrensBookAssets,
      "front_cover"
    );

  const illustrationsByUnit =
    childrensBookIllustrationsByUnit(
      childrensBookAssets
    );

  const unitDiagnostics =
    useMemo(
      () =>
        buildChildrensBookUnitDiagnostics(
          projectPlan,
          childrensBookAssets,
          {
            lang: locale,
          }
        ),
      [
        projectPlan,
        childrensBookAssets,
        locale,
      ]
    );
    const manuscriptDiagnosticText =
      summarizeChildrensBookDiagnostics(
        unitDiagnostics.manuscript,
        {
          lang: locale,
          context: "manuscript",
        }
      );

    const illustrationDiagnosticText =
      summarizeChildrensBookDiagnostics(
        unitDiagnostics
          .illustrationPlan,
        {
          lang: locale,
          context: "illustration",
        }
      );

    const assemblyDiagnosticText =
      summarizeChildrensBookDiagnostics(
        [
          ...unitDiagnostics.cover,
          ...unitDiagnostics.assembly,
        ],
        {
          lang: locale,
          context: "assembly",
        }
      );

    const readinessDiagnosticText =
      summarizeChildrensBookDiagnostics(
        [
          ...unitDiagnostics.manuscript,
          ...unitDiagnostics
            .illustrationPlan,
          ...unitDiagnostics.cover,
          ...unitDiagnostics.assembly.filter(
            (item) =>
              item.kind ===
              "missing_illustration_asset"
          ),
        ],
        {
          lang: locale,
          context: "publication",
        }
      );

    const manuscriptTextIds =
    new Set(
      manuscriptEntries
        .filter(
          (entry) =>
            textPresent(
              plainTextFromManuscriptEntry(
                entry
              )
            )
        )
        .map(
          (entry) => entry.unitId
        )
    );

  const bookAssemblyStatus =
    bookUnits.length === 0
      ? "not_started"
      : (
          Boolean(activeCover) &&
          bookUnits.every(
            (unit) =>
              (
                !unitRequiresText(
                  unit
                ) ||
                manuscriptTextIds.has(
                  unit.id
                )
              ) &&
              (
                !unit.illustrationPlan ||
                unit.illustrationPlan ===
                  "none" ||
                Boolean(
                  illustrationsByUnit[
                    unit.id
                  ]
                )
              )
          )
        )
        ? "ready"
        : "needs_attention";

  const readinessStatus =
    [
      projectStatus,
      bookMapStatus,
      coverStatus,
      manuscriptStatus,
      illustrationStatus,
      bookAssemblyStatus,
    ].every((status) => status === "ready")
      ? "ready"
      : "in_progress";

  const publicationHandoffSummary =
    useMemo(
      () =>
        buildPublicationHandoffSummary(
          projectPlan,
          childrensBookAssets
        ),
      [
        projectPlan,
        childrensBookAssets,
      ]
    );

  const publicationHandoffStarted =
    Boolean(
      publicationHandoffSummary.title ||
      publicationHandoffSummary.author ||
      publicationHandoffSummary.units
        .length ||
      publicationHandoffSummary.activeCover
    );

  const publicationHandoffStatus =
    publicationHandoffSummary.ready
      ? "ready"
      : publicationHandoffStarted
        ? "in_progress"
        : "not_started";

  const publishedOnNextScenes =
    projectPlan
      .publicationReadiness
      ?.status ===
        "published_nextscenes";

  const publishedBookStatus =
    publishedOnNextScenes
      ? "published"
      : "not_published";

  const canPublishOnNextScenes =
    !publishedOnNextScenes &&
    publicationHandoffSummary.ready &&
    typeof onPublish ===
      "function";

  const [
    publishConfirmationOpen,
    setPublishConfirmationOpen,
  ] = useState(false);

  const [
    publishing,
    setPublishing,
  ] = useState(false);

  const [
    publishError,
    setPublishError,
  ] = useState("");

  async function confirmPublishOnNextScenes() {
    if (!canPublishOnNextScenes) {
      return;
    }

    try {
      setPublishing(true);
      setPublishError("");

      const result =
        await onPublish();

      if (result?.ok === false) {
        throw new Error(
          result.error ||
          (
            isFR
              ? "Le livre n’a pas pu être publié."
              : "The book could not be published."
          )
        );
      }

      setPublishConfirmationOpen(
        false
      );

      if (
        typeof onOpenPublishedBook ===
          "function"
      ) {
        onOpenPublishedBook();
      }
    } catch (error) {
      setPublishError(
        String(
          error?.message ||
          (
            isFR
              ? "Le livre n’a pas pu être publié."
              : "The book could not be published."
          )
        ).trim()
      );
    } finally {
      setPublishing(false);
    }
  }

  const publishedAt =
    String(
      projectPlan
        .publicationReadiness
        ?.publishedAt || ""
    ).trim();

  const publishedDate = (() => {
    if (!publishedAt) {
      return "";
    }

    const date =
      new Date(publishedAt);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return publishedAt;
    }

    try {
      return new Intl.DateTimeFormat(
        isFR ? "fr" : "en",
        {
          dateStyle: "medium",
          timeStyle: "short",
        }
      ).format(date);
    } catch {
      return publishedAt;
    }
  })();

  const availableStatuses = [
    projectStatus,
    bookMapStatus,
    coverStatus,
    manuscriptStatus,
    illustrationStatus,
    bookAssemblyStatus,
    readinessStatus,
    publicationHandoffStatus,
    publishedBookStatus,
  ];

  const readyCount =
    availableStatuses.filter(
      (status) =>
        status === "ready" ||
        status === "published"
    ).length;

  const attentionCount =
    availableStatuses.filter(
      (status) =>
        status === "in_progress" ||
        status === "needs_attention" ||
        status === "not_published"
    ).length;

  const cards = [
    {
      title: isFR ? "Plan du projet" : "Project Plan",
      description: isFR
        ? "Définissez l’identité, le public, la forme, le format visuel et la méthode de travail."
        : "Define the book identity, audience, form, visual format, and working method.",
      detail: isFR ? "Les cinq étapes générales de préparation." : "The five general planning steps.",
      status: projectStatus,
      actionLabel: isFR ? "Ouvrir le plan du projet" : "Open Project Plan",
      onOpen: onOpenProjectPlan,
    },
    {
      title: isFR ? "Plan du livre" : "Book Structure",
      description: isFR
        ? "Organisez les pages, doubles pages et pages spéciales."
        : "Organise pages, two-page spreads, and special pages.",
      detail: isFR
        ? `${bookUnits.length} élément(s) · environ ${estimatedPages} page(s)`
        : `${bookUnits.length} planning unit(s) · about ${estimatedPages} page(s)`,
      status: bookMapStatus,
      actionLabel: isFR ? "Ouvrir le plan du livre" : "Open Book Structure",
      onOpen: onOpenBookMap,
    },
    {
      title: isFR ? "Plan de couverture" : "Cover Plan",
      description: isFR
        ? "Préparez le concept visuel, les textes et l’atmosphère de la couverture."
        : "Prepare the visual concept, cover text, and cover atmosphere.",
      detail: isFR
        ? "Couverture avant, numérique ou jaquette complète."
        : "Front cover, digital cover, or complete print wrap.",
      status: coverStatus,
      actionLabel: isFR ? "Ouvrir le plan de couverture" : "Open Cover Plan",
      onOpen: onOpenCoverPlan,
    },
    {
      title: isFR ? "Plan du manuscrit" : "Manuscript Plan",
      description: isFR
        ? "Rédigez le texte de chaque page ou double page à partir du Plan du livre."
        : "Write each page or spread from the Book Structure.",
      detail: (() => {
        if (
          manuscriptStatus !==
            "ready" &&
          manuscriptDiagnosticText
        ) {
          return manuscriptDiagnosticText;
        }

        const started =
          projectPlan.manuscriptPlan?.entries?.length || 0;
        const total = bookUnits.length;

        if (isFR) {
          const unitWord =
            total === 1
              ? "unité de manuscrit"
              : "unités de manuscrit";

          return `${started} sur ${total} ${unitWord} commencée${started > 1 ? "s" : ""}.`;
        }

        const unitWord =
          total === 1
            ? "manuscript unit"
            : "manuscript units";

        return `${started} of ${total} ${unitWord} started.`;
      })(),
      status: manuscriptStatus,
      actionLabel: isFR ? "Ouvrir le plan du manuscrit" : "Open Manuscript Plan",
      onOpen: onOpenManuscriptPlan,
    },
    {
      title: isFR ? "Plan des illustrations" : "Illustration Plan",
      description: isFR
        ? "Transformez les besoins visuels en directives cohérentes."
        : "Turn page-level visual needs into consistent illustration directions.",
      detail: (() => {
        if (
          illustrationStatus !==
            "ready" &&
          illustrationDiagnosticText
        ) {
          return illustrationDiagnosticText;
        }

        const started = projectPlan.illustrationPlan?.entries?.length || 0;
        const total = bookUnits.length;
        if (isFR) {
          const unitWord = total === 1 ? "unité d’illustration" : "unités d’illustration";
          return `${started} sur ${total} ${unitWord} commencée${started > 1 ? "s" : ""}.`;
        }
        const unitWord = total === 1 ? "illustration unit" : "illustration units";
        return `${started} of ${total} ${unitWord} started.`;
      })(),
      status: illustrationStatus,
      actionLabel: isFR ? "Ouvrir le plan des illustrations" : "Open Illustration Plan",
      onOpen: onOpenIllustrationPlan,
    },
    {
      title: isFR
        ? "Assemblage du livre"
        : "Book Assembly",
      description: isFR
        ? "Réunissez la couverture, le texte et les illustrations dans l’ordre du livre."
        : "Bring the cover, manuscript, and illustrations together in book order.",
      detail:
        bookAssemblyStatus ===
          "ready"
          ? (
              isFR
                ? "La couverture active sera affichée avec les pages et doubles pages."
                : "The active cover will be shown with the pages and spreads."
            )
          : (
              assemblyDiagnosticText ||
              (
                isFR
                  ? "Vérifiez les ressources manquantes du livre assemblé."
                  : "Review the missing assembled-book assets."
              )
            ),
      status: bookAssemblyStatus,
      actionLabel: isFR
        ? "Ouvrir l’assemblage du livre"
        : "Open Book Assembly",
      onOpen: onOpenBookAssembly,
    },
    {
      title: isFR ? "Préparation à la publication" : "Publication Readiness",
      description: isFR
        ? "Vérifiez la cohérence, les éléments manquants et l’état général du livre."
        : "Review consistency, missing elements, and the book’s overall readiness.",
      detail:
        readinessStatus !==
          "ready" &&
        readinessDiagnosticText
          ? readinessDiagnosticText
          : (
              isFR
                ? "Cette vérification ne publie rien automatiquement."
                : "This review does not publish anything automatically."
            ),
      status: readinessStatus,
      actionLabel: isFR
        ? "Ouvrir la préparation à la publication"
        : "Open Publication Readiness",
      onOpen: onOpenPublicationReadiness,
    },
    {
      title: isFR
        ? "Transmission pour publication"
        : "Publication Handoff",
      description: isFR
        ? "Rassemblez l’identité, le format, le manuscrit et les fichiers actifs pour l’étape extérieure suivante."
        : "Gather the identity, format, manuscript, and active files for the next external step.",
      detail: publicationHandoffSummary.ready
        ? (
            isFR
              ? "Le matériel enregistré est prêt pour la préparation extérieure."
              : "The saved material is ready for external preparation."
          )
        : (
            isFR
              ? "Complétez les éléments manquants avant la transmission."
              : "Complete the missing items before handoff."
          ),
      status: publicationHandoffStatus,
      actionLabel: isFR
        ? "Ouvrir la transmission pour publication"
        : "Open Publication Handoff",
      onOpen: onOpenPublicationHandoff,
    },
    {
      title: isFR
        ? "Livre publié"
        : "Published Book",
      description: isFR
        ? "Ouvrez l’expérience finale en lecture seule du livre publié."
        : "Open the final read-only experience of the published book.",
      detail: publishedOnNextScenes
        ? (
            publishedDate
              ? (
                  isFR
                    ? `Publié sur NextScenes le ${publishedDate}.`
                    : `Published on NextScenes ${publishedDate}.`
                )
              : (
                  isFR
                    ? "Publié sur NextScenes."
                    : "Published on NextScenes."
                )
          )
        : canPublishOnNextScenes
          ? (
              isFR
                ? "Le livre est prêt. Publiez-le sur NextScenes ou ouvrez la transmission pour revoir les détails."
                : "The book is ready. Publish it on NextScenes or open Publication Handoff to review the details."
            )
          : (
              isFR
                ? "Terminez la transmission pour publication afin de publier ce livre."
                : "Complete Publication Handoff to publish this book."
            ),
      status:
        publishedBookStatus,
      actionLabel:
        publishedOnNextScenes
          ? (
              isFR
                ? "Ouvrir le livre publié"
                : "Open Published Book"
            )
          : canPublishOnNextScenes
            ? (
                isFR
                  ? "Publier sur NextScenes"
                  : "Publish on NextScenes"
              )
            : (
                isFR
                  ? "Ouvrir la transmission pour publication"
                  : "Open Publication Handoff"
              ),
      onOpen:
        publishedOnNextScenes
          ? onOpenPublishedBook
          : canPublishOnNextScenes
            ? () => {
                setPublishError("");
                setPublishConfirmationOpen(
                  true
                );
              }
            : onOpenPublicationHandoff,
      secondaryActionLabel:
        canPublishOnNextScenes
          ? (
              isFR
                ? "Ouvrir la transmission pour publication"
                : "Open Publication Handoff"
            )
          : "",
      onSecondaryOpen:
        canPublishOnNextScenes
          ? onOpenPublicationHandoff
          : undefined,
    },
  ];

  return (
    <section
      className="ns-nbb-overview ns-nbb-workspace-page"
      aria-label={isFR ? "Vue d’ensemble du NextScenes Book Blueprint" : "NextScenes Book Blueprint overview"}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 18 }}>
        <div>
          <div className="ns-nbb-eyebrow" style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: "#b45309" }}>
            NextScenes Book Blueprint
          </div>
          <h2 className="ns-section-title ns-nbb-page-title" style={{ margin: "5px 0 8px" }}>
            {isFR ? "Vue d’ensemble du livre" : "Book Blueprint Overview"}
          </h2>
          <p className="ns-card-meta" style={{ margin: 0, maxWidth: 780, lineHeight: 1.6 }}>
            {isFR
              ? "Voyez où en est votre livre, ouvrez chaque partie disponible et comprenez ce qui reste à préparer."
              : "See where the book stands, open each available planning area, and understand what still needs attention."}
          </p>
        </div>
        <button className="ns-button ns-button-outline" type="button" onClick={onExit}>
          {isFR ? "Retour à Mes projets" : "Return to My Projects"}
        </button>
      </div>

      <div
        className="ns-nbb-card-grid ns-nbb-summary-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 18 }}
      >
        {[
          [isFR ? "Domaines disponibles" : "Available areas", "9"],
          [isFR ? "Domaines prêts" : "Areas ready", String(readyCount)],
          [isFR ? "À poursuivre" : "Need attention", String(attentionCount)],
          [isFR ? "Pages estimées" : "Estimated pages", String(estimatedPages)],
        ].map(([label, value]) => (
          <div
            key={label}
            className="ns-card ns-nbb-summary-card"
            style={{ padding: 16, borderRadius: 14 }}
          >
            <div className="ns-nbb-summary-label" style={{ fontSize: 12, fontWeight: 900, color: "#64748b" }}>{label}</div>
            <div className="ns-nbb-summary-value" style={{ marginTop: 5, fontSize: 24, fontWeight: 900, color: "#1e293b" }}>{value}</div>
          </div>
        ))}
      </div>

      <div
        className="ns-nbb-card-grid ns-nbb-overview-grid"
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}
      >
        {cards.map((card) => (
          <BlueprintCard
            key={card.title}
            {...card}
            statusText={STATUS_TEXT[locale][card.status]}
          />
        ))}
      </div>

      {publishConfirmationOpen ? (
        <div
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "grid",
            placeItems: "center",
            padding: 20,
            background:
              "rgba(15,23,42,0.56)",
          }}
          onMouseDown={(event) => {
            if (
              event.target ===
                event.currentTarget &&
              !publishing
            ) {
              setPublishConfirmationOpen(
                false
              );
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={
              isFR
                ? "Confirmer la publication"
                : "Confirm publication"
            }
            className="ns-card"
            style={{
              width:
                "min(560px, 100%)",
              padding: 22,
              borderRadius: 18,
              background: "#ffffff",
              border:
                "2px solid rgba(22,101,52,0.32)",
              boxShadow:
                "0 24px 60px rgba(15,23,42,0.26)",
            }}
          >
            <h3
              className="ns-card-title"
              style={{ margin: 0 }}
            >
              {isFR
                ? "Publier ce livre sur NextScenes ?"
                : "Publish this book on NextScenes?"}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                margin: "12px 0 0",
                lineHeight: 1.6,
              }}
            >
              {isFR
                ? "Le livre sera marqué comme publié dans NextScenes. Cette action ne crée aucun PDF, ne soumet rien à Amazon ou KDP et ne contacte aucune plateforme extérieure."
                : "The book will be marked as published inside NextScenes. This does not create a PDF, submit anything to Amazon or KDP, or contact any external platform."}
            </p>

            {publishError ? (
              <p
                role="alert"
                style={{
                  margin: "12px 0 0",
                  color: "#991b1b",
                  fontWeight: 700,
                }}
              >
                {publishError}
              </p>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 18,
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={publishing}
                onClick={() =>
                  setPublishConfirmationOpen(
                    false
                  )
                }
              >
                {isFR
                  ? "Annuler"
                  : "Cancel"}
              </button>

              <button
                className="ns-button ns-button-primary"
                type="button"
                disabled={publishing}
                aria-busy={publishing}
                onClick={
                  confirmPublishOnNextScenes
                }
              >
                {publishing
                  ? (
                      isFR
                        ? "Publication…"
                        : "Publishing…"
                    )
                  : (
                      isFR
                        ? "Confirmer et publier"
                        : "Confirm and Publish"
                    )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="ns-card" style={{ padding: 16, marginTop: 18, borderRadius: 16, background: "#fff7ed", border: "1px solid rgba(234,88,12,0.25)" }}>
        <strong>{isFR ? "Vue d’ensemble du Blueprint" : "Blueprint overview"}</strong>
        <p className="ns-card-meta" style={{ margin: "7px 0 0", lineHeight: 1.55 }}>
          {isFR
            ? "Cette vue rassemble les plans et l’état d’avancement du livre. Utilisez la navigation ci-dessus pour ouvrir chaque espace de travail."
            : "This overview brings together the book plans and progress. Use the navigation above to open each workspace."}
        </p>
      </div>

      <div style={{ marginTop: 18 }}>
        <button className="ns-button ns-button-outline" type="button" onClick={onReturn}>
          {isFR ? "Ouvrir le plan du projet" : "Open Project Plan"}
        </button>
      </div>
    </section>
  );
}
